import React, { Fragment, useEffect, useState } from 'react';
import { Button, Row, Col, Form, Table } from 'react-bootstrap';
import PropTypes from 'prop-types';

import {
  requestInsert,
  requestUpdate,
  requestDelete,
  requestInform as request
} from 'api/sfms/product/output';
import { requestList as requestMixList } from 'api/standard/code/mix';
import ActionButton from 'components/common/ActionButton';
import Search from 'components/common-sfims/form/Search';
import Select from 'components/common-sfims/form/Select';
import Modal from 'components/common-sfims/modal/Common';
import { array2ValueAndText, number2format } from 'function/convert';
import { onEnterKeyPress } from 'function/event';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import usePlantState from 'global/plantState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';
import pluck from 'utils/array/pluck';
import formatNumber from 'utils/format/number';
import numberFrom from 'utils/number/from';

/**
 * 배합비 작성 정보 관리 > 제품 생산량 - 등록/수정/조회
 */
const Inform = ({
  show,
  seq,
  setting,
  setSetting,
  prodYm,
  onHide,
  onSetting
}) => {
  /* variable */
  const { fatoryCodeSeq } = getLoginInform();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();
  const {
    plant: { hasMods: hasModPlants }
  } = usePlantState();

  /* state */
  // 제품 생산량 정보
  const [inform, setInform] = useState({});
  // 배합비 코드 정보
  const [informMix, setInformMix] = useState({});
  // 삭제 비활성화 여부
  const [isDisableDelete, setIsDisableDelete] = useState(false);
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);
  // 배합비 코드 목록
  const [mixes, setMixes] = useState([]);
  // 제품 생산량 등록/수정 요청 데이터
  const [productOutputData, setProductOutputData] = useObjReducer({
    productOutturnList: []
  });

  /* watch */
  useEffect(() => {
    // 모달이 표시된 경우
    if (!show) {
      return;
    }

    // 데이터 초기화
    setInform({});
    setProductOutputData({
      factoryCodeSeq: fatoryCodeSeq,
      mixCd: '',
      productOutturnList: [],
      prodYm
    });

    // 수정/조회 화면인 경우 제품 생산량 정보 조회 요청
    if (seq) {
      requestInform();
    }
  }, [show]);
  useEffect(() => {
    // 배합비 코드 존재 시 해당 배합비 코드 정보 설정
    setInformMix({
      productInMixList: [],
      ...mixes.find(({ mixCd } = {}) => mixCd === productOutputData.mixCd)
    });
  }, [mixes, productOutputData.mixCd]);
  useEffect(() => {
    // 해당 공장 코드 설정
    setProductOutputData({
      pltCode:
        hasModPlants.find(
          ({ seq } = {}) => seq === productOutputData.factoryCodeSeq
        )?.pltCode ?? ''
    });

    // 해당 공장 코드에 속한 배합비 코드 목록 요청
    requestMixes();
  }, [productOutputData.factoryCodeSeq]);

  /* method */
  // 제품 생산량 정보 조회 요청
  const requestInform = async () => {
    try {
      const { data = {}, code, message } = await request(seq, { prodYm });

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setInform(data);
      setProductOutputData({
        factoryCodeSeq: data.factoryCodeSeq ?? fatoryCodeSeq,
        mixCd: data.mixCd ?? '',
        productOutturnList: (data.productOutturnList ?? []).map(
          (value = {}) => ({
            ...value,
            amount: formatNumber(value.amount, ',0.00') ?? ''
          })
        )
      });
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });

      // 모달 숨김
      onHide();
    }
  };
  // 배합비 코드 목록 조회 요청
  const requestMixes = async () => {
    const { list = [] } = await requestMixList({
      factoryCodeSeqList: [productOutputData.factoryCodeSeq]
    });

    setMixes(list);
  };
  // 생산량 설정
  const setAmount = (index, amount) => {
    amount = number2format(amount, ',0.##', 99999999.99);

    if (amount === false) {
      return;
    }

    const productOutturnList = [...productOutputData.productOutturnList];

    productOutturnList[index] = {
      ...productOutturnList[index],
      amount
    };

    setProductOutputData({ productOutturnList });
  };
  // 제품 코드 설정
  const setPrdtCd = (index, prdtCd) => {
    const productOutturnList = [...productOutputData.productOutturnList];

    productOutturnList[index] = { ...productOutturnList[index] };
    productOutturnList[index].prdtCd = prdtCd;

    setProductOutputData({ productOutturnList });
  };

  /* event */
  // 배합비 코드 수정 시
  const onChangeMixCd = ({ target: { value } }) => {
    if (setting) {
      // 해당 배합비 코드에 속한 제품 목록 표시
      setProductOutputData({
        mixCd: value,
        productOutturnList: (
          mixes.find(({ mixCd } = {}) => value === mixCd)?.productInMixList ??
          []
        ).map(({ prdtCd } = {}) => ({
          prdtCd,
          amount: ''
        }))
      });
    }
  };
  // 삭제 클릭 시
  const onDeleteClick = () => {
    setConfirmModal({
      isOpen: true,
      title: 'delete',
      message: 'deleteMessage',
      isCancelBtn: true,
      btnNm: 'delete',
      async onConfirm() {
        setIsDisableDelete(true);

        try {
          const { code, message } = await requestDelete(seq, {
            productOutturnList: inform.productOutturnList.reduce(
              (result, { seq } = {}) => ({
                ...result,
                seq
              }),
              {}
            )
          });

          if (code !== RESPONSE_CODE.Success) {
            throw {
              code,
              error: message
            };
          }

          // 설정 시 이벤트 실행
          onSetting();

          // 모달 숨김
          onHide();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            message: getErrorMessage(error?.code, error?.error)
          });
          setIsDisableDelete(false);
        }
      }
    });
  };
  // 개별 삭제 클릭 시
  const onDeleteProductClick = index => {
    setConfirmModal({
      isOpen: true,
      title: 'delete',
      message: 'deleteMessage',
      isCancelBtn: true,
      btnNm: 'delete',
      onConfirm() {
        setProductOutputData({
          productOutturnList: productOutputData.productOutturnList.filter(
            (value, idx) => idx !== index
          )
        });
      }
    });
  };
  // 설정
  const onSubmit = async () => {
    if (isDisableSetting) {
      return;
    }

    setIsDisableSetting(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (
        !isValidRequired(productOutputData) ||
        productOutputData.productOutturnList.some(
          ({ prdtCd, amount } = {}) => !prdtCd || !amount
        )
      ) {
        throw {
          code: RESPONSE_CODE[seq ? 'BadRequestUpdate' : 'BadRequestInsert']
        };
      }

      // 유효하지 않는 배합비 코드 입력 시
      if (mixes.every(({ mixCd } = {}) => mixCd !== productOutputData.mixCd)) {
        throw { code: 'invalidMixCd' };
      }

      // 유효하지 않는 제품 코드 포함 시
      if (
        productOutputData.productOutturnList.some(({ prdtCd } = {}) =>
          informMix.productInMixList.every(
            ({ prdtCd: code } = {}) => code !== prdtCd
          )
        )
      ) {
        throw { code: 'invalidPrdtCd' };
      }

      const prdtCds = pluck(productOutputData.productOutturnList, 'prdtCd');

      // 제품 코드 중복 시
      if (prdtCds.length !== new Set(prdtCds).size) {
        throw { code: 'duplicatePrdtCd' };
      }

      const data = {
        ...productOutputData,
        productOutturnList: productOutputData.productOutturnList.map(
          ({ prdtCd, amount } = {}) => ({
            prdtCd,
            amount: numberFrom(amount.replaceAll(',', ''), 0)
          })
        )
      };

      if (data.productOutturnList.some(({ amount } = {}) => amount <= 0)) {
        throw { code: 'minAmount' };
      }

      if (seq) {
        const { code, message } = await requestUpdate(informMix.seq, data);

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      } else {
        const { code, message } = await requestInsert(data);

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      }

      // 설정 시 이벤트 실행
      onSetting();

      // 모달 숨김
      onHide();
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    } finally {
      setIsDisableSetting(false);
    }
  };

  /* view */
  return (
    <Modal
      show={show}
      className={{ footer: 'align-items-center justify-content-center' }}
      style={{
        maxHeight: '600px',
        overflowY: 'auto'
      }}
      size="xl"
      onHide={onHide}
    >
      <Fragment key="header">
        {getText(
          setting ? (seq ? 'updateOutput' : 'insertOutput') : 'informOutput'
        )}
      </Fragment>
      <Fragment key="body">
        <Row className="mb-3">
          <Col sm={6}>
            <Form.Group>
              <Form.Label>
                {!seq && '* '}
                {getText('pltCode')}
              </Form.Label>
              {!seq ? (
                <Select
                  datas={array2ValueAndText(hasModPlants, 'seq', 'pltCode')}
                  value={productOutputData.factoryCodeSeq}
                  className="w-100"
                  onChange={({ target: { value } }) => {
                    setProductOutputData({ factoryCodeSeq: Math.floor(value) });
                  }}
                />
              ) : (
                <Form.Control
                  readOnly
                  size="sm"
                  type="text"
                  value={inform.pltCode}
                />
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col sm={4}>
            <Form.Group>
              <Form.Label>
                {setting && '* '}
                {getText('mixCd')}
              </Form.Label>
              <Form.Control
                readOnly={seq}
                size="sm"
                type="text"
                value={productOutputData.mixCd}
                autoFocus={!seq}
                onChange={onChangeMixCd}
                onKeyUp={event => onEnterKeyPress(event, onSubmit)}
              />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group>
              <Form.Label>{getText('mixNm')}</Form.Label>
              <Form.Control
                readOnly
                size="sm"
                type="text"
                value={informMix.mixNm ?? ''}
              />
            </Form.Group>
          </Col>
          <Col sm={2}>
            <Form.Group>
              <Form.Label>{getText('amount')} (kg)</Form.Label>
              <Form.Control
                readOnly
                size="sm"
                type="text"
                value={formatNumber(
                  productOutputData.productOutturnList.reduce(
                    (result, { amount = '' } = {}) =>
                      result + numberFrom(amount.replaceAll(',', ''), 0),
                    0
                  ),
                  ',0.00'
                )}
                className="text-end"
              />
            </Form.Group>
          </Col>
        </Row>
        <Table bordered className="fs--1 text-center">
          <thead className="bg-200 text-700 align-middle border-300">
            <tr>
              <th style={{ width: '35%' }}>
                {setting && '* '}
                {getText('prdtCd')}
              </th>
              <th>{getText('prdtNm')}</th>
              <th style={{ width: '14%' }}>
                {setting && '* '}
                {getText('amount')} (kg)
              </th>
              {setting && (
                <th style={{ width: '6%' }}>
                  {/* 해당 배합비 코드에 속한 제품 목록 수만큼만 추가 가능 */}
                  {informMix.productInMixList.length >
                    productOutputData.productOutturnList.length && (
                    <ActionButton
                      title={getText('insert')}
                      icon="plus"
                      variant="action"
                      className="p-0 lh-1"
                      onClick={() => {
                        setProductOutputData({
                          productOutturnList: [
                            ...productOutputData.productOutturnList,
                            {
                              prdtCd: '',
                              amount: ''
                            }
                          ]
                        });
                      }}
                    />
                  )}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {productOutputData.productOutturnList.map(
              ({ prdtCd, amount } = {}, index) => (
                <tr key={`productOutturnList${index}`}>
                  <td>
                    {setting ? (
                      <Search
                        datas={array2ValueAndText(
                          informMix.productInMixList
                            .filter(
                              ({ prdtCd } = {}) =>
                                !pluck(
                                  productOutputData.productOutturnList,
                                  'prdtCd'
                                ).includes(prdtCd)
                            )
                            .map((value = {}) => ({
                              ...value,
                              text: `${value.prdtCd} (${value.prdtNm})`
                            })),
                          'prdtCd',
                          'text'
                        )}
                        value={prdtCd}
                        setValue={prdtCd => setPrdtCd(index, prdtCd)}
                        onKeyUp={event => onEnterKeyPress(event, onSubmit)}
                      />
                    ) : (
                      <Form.Control
                        readOnly
                        size="sm"
                        type="text"
                        value={prdtCd}
                        className="text-center"
                      />
                    )}
                  </td>
                  <td>
                    <Form.Control
                      readOnly
                      size="sm"
                      type="text"
                      value={
                        informMix.productInMixList.find(
                          ({ prdtCd: code } = {}) => code === prdtCd
                        )?.prdtNm ?? ''
                      }
                      className="text-center"
                    />
                  </td>
                  <td className="align-middle">
                    <Form.Control
                      readOnly={!setting}
                      size="sm"
                      type="text"
                      value={amount}
                      className="text-end"
                      onChange={({ target: { value } }) => {
                        setAmount(index, value);
                      }}
                    />
                  </td>
                  {setting && (
                    <td className="align-middle">
                      <ActionButton
                        title={getText('delete')}
                        icon="times"
                        variant="action"
                        className="p-0 lh-1"
                        onClick={() => onDeleteProductClick(index)}
                      />
                    </td>
                  )}
                </tr>
              )
            )}
          </tbody>
        </Table>
      </Fragment>
      <Fragment key="footer">
        <Button variant="secondary" onClick={onHide}>
          {getText(setting ? 'cancel' : 'close')}
        </Button>
        {setting ? (
          <Button
            disabled={isDisableSetting}
            className="ms-2"
            onClick={onSubmit}
          >
            {getText('ok')}
          </Button>
        ) : (
          <>
            <Button
              variant="success"
              className="ms-2"
              onClick={() => setSetting(true)}
            >
              {getText('update')}
            </Button>
            <Button
              disabled={isDisableDelete}
              variant="danger"
              className="ms-2"
              onClick={onDeleteClick}
            >
              {getText('delete')}
            </Button>
          </>
        )}
      </Fragment>
    </Modal>
  );
};

Inform.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 배합비 코드 번호
  seq: PropTypes.number,

  // 설정 여부
  setting: PropTypes.bool,

  // 설정 여부 설정 함수
  setSetting: PropTypes.func,

  // 월
  prodYm: PropTypes.string,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func,

  // 설정 시 이벤트
  onSetting: PropTypes.func
};
Inform.defaultProps = {
  show: false,
  setting: false
};

export default Inform;
